import React, { useState, useEffect } from 'react';
import { PixelGlyph } from '../common/PixelGlyph';
import { MagneticButton } from '../common/MagneticButton';
import { ShieldCheck, Menu, X } from 'lucide-react';

export const HeaderNav: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { label: 'Manifesto', href: '#manifesto' },
    { label: 'Specimens', href: '#specimens' },
    { label: 'Architecture', href: '#architecture' },
    { label: 'Playground', href: '#playground' },
    { label: 'Benchmarks', href: '#benchmarks' },
  ];

  // Prevent background scroll when mobile drawer is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  return (
    <>
      <header className="fixed top-5 left-0 right-0 z-50 px-4 flex justify-center pointer-events-none">
        <nav
          className="pointer-events-auto w-full max-w-5xl rounded-full bg-[#0e0e13]/95 border border-white/10 shadow-2xl px-3 sm:px-4 py-2 sm:py-2.5 flex items-center justify-between transition-all duration-300 hover:border-white/20"
          aria-label="Main Navigation"
        >
          {/* Logo & Identity */}
          <a
            href="#"
            className="flex items-center gap-2.5 group pl-2 text-cream-100 font-display font-bold text-lg tracking-wider"
            data-cursor="home"
          >
            <span className="w-5 h-5 rounded-md bg-gradient-to-tr from-sunset-gold to-sunset-crimson flex items-center justify-center text-[11px] font-mono text-espresso-950 font-black">
              E
            </span>
            <span className="flex items-center gap-1.5">
              EXAMPLE
              <span className="text-[10px] font-mono font-normal tracking-widest text-sunset-gold">
                [01]
              </span>
            </span>
          </a>

          {/* Central Domain Verification Capsule */}
          <div className="hidden md:flex items-center gap-2 px-3 py-1 rounded-full bg-espresso-950/70 border border-white/[0.08] text-xs font-mono text-cream-300 select-none">
            <ShieldCheck className="w-3.5 h-3.5 text-sunset-gold" />
            <span className="text-cream-100 font-medium">example.th157le.ru</span>
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          </div>

          {/* Desktop Links */}
          <div className="hidden lg:flex items-center gap-6 text-xs font-medium text-cream-muted">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="hover:text-cream-100 transition-colors py-1 relative group"
                data-cursor="interactive"
              >
                {link.label}
                <span className="absolute bottom-0 left-0 w-0 h-px bg-sunset-gold transition-all duration-300 group-hover:w-full" />
              </a>
            ))}
          </div>

          {/* Right Action & Mobile Toggle */}
          <div className="flex items-center gap-2">
            <div className="hidden sm:block">
              <MagneticButton
                size="sm"
                variant="sunset"
                href="#playground"
                showArrow={true}
              >
                Launch Playground
              </MagneticButton>
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-full hover:bg-white/10 text-cream-100 transition-colors pointer-events-auto cursor-pointer"
              aria-label="Toggle navigation menu"
            >
              {mobileMenuOpen ? (
                <X className="w-5 h-5 text-sunset-gold" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile Drawer (Frosted Glass Glassmorphism Backdrop) */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 flex flex-col lg:hidden animate-in fade-in duration-200"
          style={{
            backgroundColor: 'rgba(7, 7, 9, 0.85)',
            backdropFilter: 'blur(28px)',
            WebkitBackdropFilter: 'blur(28px)',
          }}
        >
          <div className="max-w-md mx-auto w-full flex flex-col h-full min-h-0 pt-24 pb-8 px-6">
            {/* Top Indicator */}
            <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-sunset-gold pb-3 mb-2 border-b border-white/10">
              <span className="flex items-center gap-2">
                <PixelGlyph name="pulse-dot" size={8} />
                <span>EXHIBIT DIRECTORY</span>
              </span>
              <span className="text-cream-dim">[05 SECTIONS]</span>
            </div>

            {/* Scrollable Nav Links */}
            <div className="divide-y divide-white/10 flex-1 min-h-0 overflow-y-auto no-scrollbar py-2">
              {navLinks.map((link, idx) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center justify-between py-4 text-base sm:text-lg font-display font-semibold text-cream-100 hover:text-sunset-gold transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-mono text-sunset-gold/70">
                      0{idx + 1}.
                    </span>
                    <span className="group-hover:translate-x-1 transition-transform">
                      {link.label}
                    </span>
                  </div>
                  <div className="w-7 h-7 rounded-full bg-white/5 group-hover:bg-sunset-gold/20 flex items-center justify-center transition-colors">
                    <PixelGlyph name="arrow-up-right" size={12} color="#d4af37" />
                  </div>
                </a>
              ))}
            </div>

            {/* Bottom Actions & Direct Contacts */}
            <div className="pt-4 border-t border-white/10 space-y-3 mt-auto">
              <MagneticButton
                variant="sunset"
                size="md"
                className="w-full justify-center"
                href="#playground"
                onClick={() => setMobileMenuOpen(false)}
              >
                Try Live Playground
              </MagneticButton>

              <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                <a
                  href="https://t.me/TH157LECLAW"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2.5 rounded-xl bg-espresso-900/90 border border-white/10 hover:border-sunset-gold/50 flex items-center justify-between text-cream-200 hover:text-white transition-colors"
                >
                  <span className="text-[11px] truncate">@TH157LECLAW</span>
                  <PixelGlyph name="arrow-up-right" size={10} color="#ffaf01" />
                </a>

                <a
                  href="mailto:support@th157le.ru"
                  className="p-2.5 rounded-xl bg-espresso-900/90 border border-white/10 hover:border-sunset-gold/50 flex items-center justify-between text-cream-200 hover:text-white transition-colors"
                >
                  <span className="text-[11px] truncate">support@...</span>
                  <PixelGlyph name="arrow-up-right" size={10} color="#ffaf01" />
                </a>
              </div>

              <div className="pt-1 flex items-center justify-between text-[10px] font-mono text-cream-dim">
                <span>STATUS: ACCELERATED</span>
                <span className="text-gold-champagne">example.th157le.ru</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

